import { mat4, vec3 } from 'gl-matrix';
import { BoundingSphere } from '../util';
import { RenderedTexture } from '../texture';
import { Context } from '../context/Context';
import { DepthMapRenderer, DepthMode, SceneRenderer } from '../renderer';
import { Scene } from '../scene/Scene';
import { toRadian } from '../math';
import { Light, LightCamera, LightType, SHADOW_MAP_DIMENSIONS, BaseLightProperties } from './Light';

export type SpotLightProperties = BaseLightProperties & {
  innerRange: number;
  outerRange: number;
  dotInnerLimit: number;
  dotOuterLimit: number;
  direction: vec3 | number[];
};

export class SpotLight extends Light {
  public readonly type: LightType.Spot = LightType.Spot;

  public innerRange: number;
  public outerRange: number;
  public dotInnerLimit: number;
  public dotOuterLimit: number;
  public direction: vec3 | number[];
  declare public boundingSphere: BoundingSphere;

  declare public camera: SpotLightCamera;
  private depthRenderer?: DepthMapRenderer;

  constructor(properties: SpotLightProperties) {
    super(properties);
    this.innerRange = properties.innerRange;
    this.outerRange = properties.outerRange;
    this.dotInnerLimit = properties.dotInnerLimit;
    this.dotOuterLimit = properties.dotOuterLimit;
    this.direction = properties.direction;
    this.boundingSphere = new BoundingSphere(this.position, this.outerRange);
    this.camera = new SpotLightCamera(SHADOW_MAP_DIMENSIONS, this);
    this.camera.setup();
  }

  public updateBounds(): void {
    this.boundingSphere.position = this.position;
    this.markDirty();
  }

  public renderShadowMap(ctx: Context): void {
    const scene = ctx.engine.activeScene!;
    const im = scene.instanceManager;

    if (!this.depthRenderer) {
      this.depthRenderer = new DepthMapRenderer(DepthMode.CameraDistance);
      this.depthRenderer.init(ctx);
    }
    this.depthRenderer.instanceManager = im;
    this.render2d(ctx, this.depthRenderer, scene);
  }

  private render2d(ctx: Context, renderer: SceneRenderer, scene: Scene) {
    if (!this.shadowMap) {
      const tex = new RenderedTexture(true, SHADOW_MAP_DIMENSIONS);
      tex.init(ctx);
      this.shadowMap = tex;
    }
    this.shadowMap.renderTexture(renderer, scene, this.camera);
  }

  public packGpuData(data: Float32Array, o: number): void {
    this.packCommon(data, o);
    data[o + 7] = this.outerRange * this.outerRange; // outerRange² for fast distance check

    // Texel 2: direction
    data[o + 8] = this.direction[0] as number;
    data[o + 9] = this.direction[1] as number;
    data[o + 10] = this.direction[2] as number;
    data[o + 11] = 0;

    // Texel 3: ranges and cone limits
    data[o + 12] = this.innerRange;
    data[o + 13] = this.outerRange;
    data[o + 14] = Math.cos(toRadian(this.dotInnerLimit));
    data[o + 15] = Math.cos(toRadian(this.dotOuterLimit));
  }
}

export class SpotLightCamera extends LightCamera<SpotLight> {
  protected determineTargetPosition(): vec3 {
    const target = vec3.create();
    vec3.scale(target, this.light.direction as vec3, 100);
    vec3.add(target, target, this.light.position as vec3);
    return target;
  }

  protected determineUp(): vec3 | number[] {
    return this.computeNonParallelUp();
  }

  protected determineProjection(): mat4 {
    const matrix = mat4.create();
    mat4.perspective(matrix, toRadian(120), this.aspectRatio, 0.1, this.light.outerRange);
    return matrix;
  }
}
